import { zodResolver } from "@hookform/resolvers/zod";
import { signUpSchema, type SignUpInput } from "@iagenix/shared";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { AuthLayout } from "@/components/auth-layout";
import { FormField } from "@/components/form-field";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { authClient, authErrorMessage } from "@/lib/auth-client";

export const Route = createFileRoute("/cadastro")({
  validateSearch: z.object({ redirecionar: z.string().optional() }),
  component: CadastroPage,
});

function CadastroPage() {
  const { redirecionar } = Route.useSearch();
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string | null>(null);
  const form = useForm<SignUpInput>({
    resolver: zodResolver(signUpSchema),
    defaultValues: { name: "", email: "", password: "" },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null);
    const { error } = await authClient.signUp.email({
      name: values.name,
      email: values.email,
      password: values.password,
    });
    if (error) {
      setFormError(authErrorMessage(error.code, error.message));
      return;
    }
    const target =
      redirecionar && redirecionar.startsWith("/") && !redirecionar.startsWith("//")
        ? redirecionar
        : "/app";
    await navigate({ to: target });
  });

  return (
    <AuthLayout title="Criar conta" description="Leva menos de um minuto.">
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        {formError ? (
          <Alert variant="destructive">
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        ) : null}
        <FormField id="name" label="Seu nome" error={form.formState.errors.name?.message}>
          <Input
            id="name"
            autoComplete="name"
            placeholder="Maria Silva"
            {...form.register("name")}
          />
        </FormField>
        <FormField id="email" label="E-mail" error={form.formState.errors.email?.message}>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="voce@agencia.com.br"
            {...form.register("email")}
          />
        </FormField>
        <FormField
          id="password"
          label="Senha"
          error={form.formState.errors.password?.message}
          hint="Pelo menos 8 caracteres."
        >
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            {...form.register("password")}
          />
        </FormField>
        <Button type="submit" className="w-full" loading={form.formState.isSubmitting}>
          Criar conta
        </Button>
        <p className="text-center text-xs text-muted-foreground">
          Ao criar a conta você concorda com os{" "}
          <Link to="/termos" className="underline">
            Termos de Uso
          </Link>{" "}
          e a{" "}
          <Link to="/privacidade" className="underline">
            Política de Privacidade
          </Link>
          .
        </p>
        <p className="text-center text-sm text-muted-foreground">
          Já tem conta?{" "}
          <Link
            to="/entrar"
            search={{ redirecionar }}
            className="font-medium text-foreground underline-offset-4 hover:underline"
          >
            Entrar
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
